import { useEffect, useState } from "react";
import { clsx } from "clsx";
import { ChevronLeft, ChevronRight, Star, Upload, X } from "lucide-react";

// Etsy liste sayfasi gorunumu (6. adim: kurulacak taslak, 7. adim: panel taslagi). Galeri
// kucuk resimleri + buyuk gorsel; buyuk gorsele tiklayinca tam ekran, sag/sol (ok tuslari) ile gezinilir.
// Acilir listeler secilebilir (yalniz degerleri gormek icin; hicbir yere kaydedilmez).

export interface EtsyPreviewQuestion { type: string; text: string; instructions?: string; required: boolean; options?: string[]; maxChars?: number }
export interface EtsyPreviewData {
  images: string[];
  title: string;
  price: number | null;
  /** Fiyat secenege gore degisiyor ("$45.00+") */
  priceFrom?: boolean;
  variations: Array<{ name: string; options: string[] }>;
  questions: EtsyPreviewQuestion[];
  description: string;
  tags: string[];
}

/** Tam ekran gorsel: sag/sol ok, Esc kapatir */
export function Lightbox({ images, index, onClose }: { images: string[]; index: number; onClose: () => void }) {
  const [i, setI] = useState(index);
  const n = images.length;
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowRight") setI((x) => (x + 1) % n);
      else if (e.key === "ArrowLeft") setI((x) => (x - 1 + n) % n);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [n, onClose]);
  if (!n) return null;
  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/85" onClick={onClose} role="dialog" aria-modal="true" aria-label="Görsel">
      <img src={images[i]} alt={`${i + 1}. görsel`} className="max-h-[90vh] max-w-[90vw] object-contain" onClick={(e) => e.stopPropagation()} />
      <button onClick={onClose} aria-label="Kapat" className="absolute right-4 top-4 rounded-full bg-white/15 p-2 text-white hover:bg-white/25"><X className="h-6 w-6" /></button>
      {n > 1 && (
        <>
          <button onClick={(e) => { e.stopPropagation(); setI((x) => (x - 1 + n) % n); }} aria-label="Önceki" className="absolute left-4 top-1/2 -translate-y-1/2 rounded-full bg-white/15 p-3 text-white hover:bg-white/25"><ChevronLeft className="h-7 w-7" /></button>
          <button onClick={(e) => { e.stopPropagation(); setI((x) => (x + 1) % n); }} aria-label="Sonraki" className="absolute right-4 top-1/2 -translate-y-1/2 rounded-full bg-white/15 p-3 text-white hover:bg-white/25"><ChevronRight className="h-7 w-7" /></button>
        </>
      )}
      <span className="absolute bottom-4 left-1/2 -translate-x-1/2 rounded-full bg-black/60 px-3 py-1 text-sm text-white">{i + 1} / {n}</span>
    </div>
  );
}

export function EtsyPreview({ data }: { data: EtsyPreviewData }) {
  const [active, setActive] = useState(0);
  const [zoom, setZoom] = useState<number | null>(null);
  const { images } = data;
  const shown = images[Math.min(active, Math.max(0, images.length - 1))];
  const step = (d: number) => setActive((a) => (a + d + images.length) % Math.max(1, images.length));
  return (
    <div className="grid gap-6 rounded-xl border border-gray-200 bg-white p-4 lg:grid-cols-[1fr_360px]">
      <div className="flex gap-3">
        <div className="flex max-h-[520px] w-20 flex-shrink-0 flex-col gap-2 overflow-y-auto pr-1">
          {images.map((u, i) => (
            <button key={u + i} onClick={() => setActive(i)} aria-label={`${i + 1}. görsel`}
              className={clsx("relative block aspect-square w-full flex-shrink-0 overflow-hidden rounded-lg border-2", i === active ? "border-gray-900" : "border-transparent hover:border-gray-300")}>
              <img src={u} alt="" loading="lazy" className="h-full w-full object-cover" />
              {i === 0 && <Star className="absolute left-1 top-1 h-3.5 w-3.5 fill-amber-400 text-amber-400 drop-shadow" aria-label="Ana görsel" />}
            </button>
          ))}
        </div>
        <div className="min-w-0 flex-1">
          {shown ? (
            <div className="group relative overflow-hidden rounded-xl bg-gray-50">
              <button onClick={() => setZoom(active)} className="block w-full cursor-zoom-in" aria-label="Büyüt">
                <img src={shown} alt="" className="max-h-[520px] w-full object-contain" />
              </button>
              {images.length > 1 && (
                <>
                  <button onClick={() => step(-1)} aria-label="Önceki görsel" className="absolute left-2 top-1/2 -translate-y-1/2 rounded-full bg-white/90 p-2 shadow hover:bg-white"><ChevronLeft className="h-5 w-5" /></button>
                  <button onClick={() => step(1)} aria-label="Sonraki görsel" className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full bg-white/90 p-2 shadow hover:bg-white"><ChevronRight className="h-5 w-5" /></button>
                </>
              )}
              <span className="absolute bottom-2 left-2 rounded-full bg-black/60 px-2 py-0.5 text-xs text-white">{active + 1} / {images.length}</span>
            </div>
          ) : <div className="flex aspect-square items-center justify-center rounded-xl border border-dashed border-gray-200 text-sm text-gray-500">Görsel yok</div>}
        </div>
      </div>

      <div className="space-y-4">
        {data.price != null && Number.isFinite(data.price) && <p className="text-2xl font-semibold text-gray-900">${Number(data.price).toFixed(2)}{data.priceFrom ? "+" : ""}</p>}
        <h2 className="text-base leading-snug text-gray-900">{data.title}</h2>
        <p className={clsx("text-xs", data.title.length > 140 ? "text-red-600" : "text-gray-400")}>{data.title.length}/140 karakter</p>
        {data.variations.slice(0, 3).map((v, i) => (
          <div key={i}>
            <p className="mb-1 text-sm font-medium text-gray-900">{v.name}</p>
            <select aria-label={v.name} defaultValue="" className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700">
              <option value="" disabled>Select an option ({v.options.length})</option>
              {v.options.map((o, k) => <option key={k} value={o}>{o}</option>)}
            </select>
          </div>
        ))}
        {data.questions.length > 0 && (
          <div className="space-y-3 rounded-lg border border-gray-200 p-3">
            <p className="text-sm font-medium text-gray-900">Add your personalization</p>
            {data.questions.map((q, i) => (
              <div key={i}>
                <p className="text-sm text-gray-900">{q.text}{q.required ? "" : <span className="text-gray-500"> (optional)</span>}</p>
                {q.instructions && <p className="whitespace-pre-line text-xs text-gray-600">{q.instructions}</p>}
                {q.type === "dropdown" ? (
                  <select aria-label={q.text} defaultValue="" className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm"><option value="" disabled>Select an option ({q.options?.length ?? 0})</option>{q.options?.map((o, k) => <option key={k} value={o}>{o}</option>)}</select>
                ) : q.type.endsWith("upload") ? (
                  <span className="mt-1 inline-flex items-center gap-1.5 rounded-full border border-gray-300 px-3 py-1 text-xs text-gray-700"><Upload className="h-3.5 w-3.5" /> Upload</span>
                ) : (
                  <div className="mt-1 flex h-14 items-end justify-end rounded-lg border border-gray-300 p-1.5 text-xs text-gray-400">{q.maxChars ? `0/${q.maxChars}` : ""}</div>
                )}
              </div>
            ))}
          </div>
        )}
        <button disabled className="w-full rounded-full bg-gray-900 py-3 text-sm font-semibold text-white">Add to cart</button>
      </div>

      <div className="space-y-3 border-t border-gray-100 pt-4 lg:col-span-2">
        <p className="text-sm font-semibold text-gray-900">Item details</p>
        {data.description.trim() ? <p className="whitespace-pre-line text-sm text-gray-700">{data.description}</p> : <p className="text-sm text-gray-500">Açıklama yok.</p>}
        {data.tags.length ? (
          <div className="flex flex-wrap gap-1">
            {data.tags.map((t) => <span key={t} className={clsx("rounded-full px-2 py-0.5 text-xs", t.length > 20 ? "bg-red-50 text-red-700" : "bg-gray-100 text-gray-600")}>{t}</span>)}
            <span className={clsx("text-xs", data.tags.length > 13 ? "text-red-600" : "text-gray-400")}>{data.tags.length}/13</span>
          </div>
        ) : <p className="text-xs text-amber-700">Etiket yok.</p>}
      </div>
      {zoom !== null && <Lightbox images={images} index={zoom} onClose={() => setZoom(null)} />}
    </div>
  );
}
